'use client';

import './styles.css';

import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import React, {
    CSSProperties,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';

import { cn } from '@/utils/cn';

import { Arrow } from './components/arrow';
import {
    NextButton,
    PrevButton,
    usePrevNextButtons,
} from './components/arrow-button';
import { DotButton, useDotButton } from './components/dot-button';
import { CustomControlsProps } from './types';

type Props = PropsWithChildren & {
    CustomControls?: (props: CustomControlsProps) => ReactNode;
    children: ReactNode[];
    className?: string;
    controls: ('arrows' | 'dots' | 'progressbar' | 'circleArrows')[];
    hasDimmedEdges?: boolean;
    onSlideChange?: (index: number) => void;
    options?: EmblaOptionsType;
    slideSpacing?: string;
};

export const CarouselSlide = ({
    children,
    className,
}: PropsWithChildren<{ className?: string }>) => (
    <div className={cn(`embla__slide`, className)}>{children}</div>
);

export const Carousel = ({
    CustomControls,
    children,
    className,
    controls,
    hasDimmedEdges,
    onSlideChange,
    options,
    slideSpacing,
}: Props) => {
    const [emblaRef, emblaApi] = useEmblaCarousel(
        { ...options, loop: false, skipSnaps: true },
        [WheelGesturesPlugin()],
    );

    const [scrollProgress, setScrollProgress] = useState(0);

    const { onDotButtonClick, scrollSnaps, selectedIndex } =
        useDotButton(emblaApi);

    useEffect(() => {
        onSlideChange?.(selectedIndex);
    }, [onSlideChange, selectedIndex]);

    const {
        nextBtnDisabled,
        onNextButtonClick,
        onPrevButtonClick,
        prevBtnDisabled,
    } = usePrevNextButtons(emblaApi);

    const onScroll = useCallback((api: EmblaCarouselType) => {
        const progress = Math.max(0, Math.min(1, api.scrollProgress()));
        setScrollProgress(progress * 100);
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onScroll(emblaApi);
        emblaApi
            .on('reInit', onScroll)
            .on('scroll', onScroll)
            .on('slideFocus', onScroll);

        return () => {
            emblaApi.off('reInit', onScroll);
            emblaApi.off('scroll', onScroll);
            emblaApi.off('slideFocus', onScroll);
        };
    }, [emblaApi, onScroll]);

    const hasControls = controls.length > 0;
    const hasArrows = controls.includes('arrows');
    const hasCircleArrows = controls.includes('circleArrows');
    const hasDots = controls.includes('dots');
    const hasProgressbar = controls.includes('progressbar');

    return (
        <div
            style={
                slideSpacing
                    ? ({ '--slide-spacing': slideSpacing } as CSSProperties)
                    : undefined
            }
            className={cn('embla relative', className)}
        >
            {CustomControls && (
                <div
                    className={`
                      flex justify-center

                      xxl:container xxl:mx-auto
                    `}
                >
                    <CustomControls
                        onButtonClick={onDotButtonClick}
                        scrollSnaps={scrollSnaps}
                        selectedIndex={selectedIndex}
                    />
                </div>
            )}

            <div
                className={`
                  embla__viewport px-2

                  lg:px-12

                  sm:px-6

                  xl:px-20

                  xxl:px-carousel
                `}
                ref={emblaRef}
            >
                <div className={`embla__container`}>{children}</div>
            </div>

            {hasDimmedEdges && (
                <>
                    <div
                        id="dimming-left"
                        className={`
                          pointer-events-none absolute bottom-0 left-0 top-20
                          z-10 hidden bg-gradient-to-r from-primary-black

                          lg:w-[20vw]

                          md:block md:w-[10vw]

                          xl:w-[30vw]
                        `}
                    />
                    <div
                        id="dimming-right"
                        className={`
                          pointer-events-none absolute bottom-0 right-0 top-20
                          z-10 hidden h-full bg-gradient-to-l from-primary-black

                          lg:w-[20vw]

                          md:block md:w-[10vw]

                          xl:w-[30vw]
                        `}
                    />
                </>
            )}

            {hasControls && (
                <>
                    {hasCircleArrows && (
                        <>
                            <button
                                className={cn(
                                    `
                                      absolute left-0 top-[45%] z-20 ml-4 flex
                                      size-12 -translate-y-1/2 items-center
                                      justify-center rounded-full
                                      bg-primary-white text-primary-black
                                      transition-opacity duration-200

                                      active:scale-100

                                      hover:scale-110

                                      md:ml-14

                                      sm:ml-8 sm:size-16
                                    `,
                                    {
                                        'pointer-events-none opacity-10':
                                            prevBtnDisabled,
                                    },
                                )}
                                onClick={onPrevButtonClick}
                                disabled={prevBtnDisabled}
                            >
                                <Arrow />
                            </button>

                            <button
                                className={cn(
                                    `
                                      absolute right-0 top-[45%] z-20 mr-4 flex
                                      size-12 -translate-y-1/2 rotate-180
                                      items-center justify-center rounded-full
                                      bg-primary-white text-primary-black
                                      transition-opacity duration-200

                                      active:scale-100

                                      hover:scale-110

                                      md:mr-14

                                      sm:mr-8 sm:size-16
                                    `,
                                    {
                                        'pointer-events-none opacity-10':
                                            nextBtnDisabled,
                                    },
                                )}
                                onClick={onNextButtonClick}
                                disabled={nextBtnDisabled}
                            >
                                <Arrow />
                            </button>
                        </>
                    )}

                    {(hasArrows || hasProgressbar || hasDots) && (
                        <div
                            className={`
                              mt-10 flex justify-center

                              xxl:container xxl:mx-auto
                            `}
                        >
                            {hasArrows && (
                                <div className="embla__buttons">
                                    <PrevButton
                                        onClick={onPrevButtonClick}
                                        disabled={prevBtnDisabled}
                                    />
                                    <NextButton
                                        onClick={onNextButtonClick}
                                        disabled={nextBtnDisabled}
                                    />
                                </div>
                            )}

                            {hasProgressbar && (
                                <div className="embla__progress">
                                    <div
                                        className="embla__progress__bar"
                                        style={{
                                            transform: `translate3d(${scrollProgress}%,0px,0px)`,
                                        }}
                                    />
                                </div>
                            )}

                            {hasDots && (
                                <div className="embla__dots">
                                    {scrollSnaps.map((_, index) => (
                                        <DotButton
                                            key={`DotButton-${index}`}
                                            onClick={() =>
                                                onDotButtonClick(index)
                                            }
                                            className={cn('embla__dot', {
                                                'embla__dot--selected':
                                                    index === selectedIndex,
                                            })}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
